/**
 * Analytics — Firebase Web (GA4), même schéma d’événements que le mobile (Capacitor).
 *
 * Principes (alignés feature/mobile) :
 * - Échecs silencieux partout : jamais de throw vers l’UI, jamais bloquer le flux.
 * - Noms d’événements identiques au mobile : screen_view, view_item, login, search, etc.
 * - Comptes admin / internes : pas d’envoi (rôles exclus + option suffixe email + désactivation collecte).
 * - Trafic équipe dans GA4 : compléter par « Trafic interne » / filtres dans la console GA4
 *   (Admin GA4 → flux de données → plus de balises → définir le trafic interne).
 * - DebugView : `VITE_FIREBASE_ANALYTICS_DEBUG=true` ou `VITE_ANALYTICS_DEBUG_MODE=true`
 *   puis Firebase Console → Analytics → DebugView (événements en quasi temps réel).
 *
 * Chargement `firebase/app` + `firebase/analytics` uniquement après `window` `load`.
 * Config : littéraux projet Awlo ci-dessous ; surcharge via `VITE_FIREBASE_*` / `NEXT_PUBLIC_FIREBASE_*`.
 * (Ne pas utiliser `process.env.FIREBASE_*` ici : non injecté par Vite côté client sans `define`.)
 *
 * `measurementId` : obligatoirement celui du flux GA4 **Web** (même bloc que l’app Web dans Firebase) ;
 * pas le Measurement ID des flux iOS/Android. `appId` doit contenir `:web:` (contrôle dans buildFirebaseConfig).
 *
 * Env optionnels :
 * - VITE_ANALYTICS_VERBOSE=true — logs console (init OK / config manquante / non supporté / erreur).
 * - VITE_ANALYTICS_DISABLED=true — désactive entièrement le chargement / les events.
 * - VITE_ANALYTICS_SKIP_ROLES=Administrator,Support — rôles Directus additionnels à exclure.
 * - VITE_ANALYTICS_SKIP_EMAIL_SUFFIX=@votredomaine.com — ne pas tracker ces emails.
 */

/**
 * Défauts web Awlo — surcharger par `VITE_FIREBASE_*` (éviter `process.env.FIREBASE_*` : non fiable côté client Vite).
 * `measurementId` = G-xxxxxxxx du flux **Web** GA4 (pas iOS/Android).
 */

import { setAnalyticsCollectionEnabled } from 'firebase/analytics';
const DEFAULT_FIREBASE_WEB_CONFIG = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_AUTH_DOMAIN,
  projectId: process.env.FIREBASE_PROJECT_ID,
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.FIREBASE_APP_ID,
  measurementId: process.env.FIREBASE_MEASUREMENT_ID,
};

function readEnv(suffix) {
  try {
    const env = import.meta.env;
    const raw = env[`VITE_${suffix}`] ?? env[`NEXT_PUBLIC_${suffix}`];
    if (raw === undefined || raw === null) return undefined;
    const s = String(raw).trim();
    return s === "" ? undefined : s;
  } catch {
    return undefined;
  }
}

function isAnalyticsVerbose() {
  try {
    const v = readEnv("ANALYTICS_VERBOSE");
    return v === "true" || v === "1" || v === "yes";
  } catch {
    return false;
  }
}

function analyticsDiag(message, detail) {
  try {
    if (!isAnalyticsVerbose()) return;
    if (typeof console !== "undefined" && console.info) {
      console.info("[Awlo Analytics]", message, detail !== undefined ? detail : "");
    }
  } catch {
    /* silencieux */
  }
}

/**
 * Réutilise une app Firebase déjà créée pour le même `projectId`, sinon crée l’app par défaut
 * ou une app nommée si une autre app (ex. config placeholder) est déjà enregistrée.
 * @param {typeof import('firebase/app')} appMod
 */
function resolveFirebaseApp(appMod, firebaseConfig) {
  const named = "awlo-web-analytics";
  const apps = appMod.getApps() || [];
  const match = apps.find(
    (a) => a?.options?.projectId === firebaseConfig.projectId,
  );
  if (match) return match;
  if (apps.length === 0) {
    return appMod.initializeApp(firebaseConfig);
  }
  try {
    return appMod.getApp(named);
  } catch {
    return appMod.initializeApp(firebaseConfig, named);
  }
}

/** Rôles Directus considérés comme staff / admin (pas de tracking), comme sur mobile à étendre. */
const DEFAULT_SKIP_TRACKING_ROLES = ["Administrator", "Admin"];

function isAnalyticsGloballyDisabled() {
  try {
    const v = readEnv("ANALYTICS_DISABLED");
    return v === "true" || v === "1" || v === "yes";
  } catch {
    return false;
  }
}

function parseSkipRolesFromEnv() {
  try {
    const raw = readEnv("ANALYTICS_SKIP_ROLES");
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

function isExcludedRoleName(roleName) {
  try {
    if (roleName == null || roleName === "") return false;
    const set = new Set([
      ...DEFAULT_SKIP_TRACKING_ROLES,
      ...parseSkipRolesFromEnv(),
    ]);
    return set.has(String(roleName).trim());
  } catch {
    return false;
  }
}

function isExcludedEmail(email) {
  try {
    const suffix = readEnv("ANALYTICS_SKIP_EMAIL_SUFFIX");
    if (!suffix || !email) return false;
    return String(email)
      .toLowerCase()
      .endsWith(String(suffix).toLowerCase());
  } catch {
    return false;
  }
}

/**
 * Profil à ne pas tracker (admin Directus, email interne, etc.).
 * @param {Record<string, unknown>} userData
 */
function isInternalUserProfile(userData) {
  try {
    if (!userData) return false;
    const role = userData.role?.name;
    if (isExcludedRoleName(role)) return true;
    if (userData.email && isExcludedEmail(userData.email)) return true;
    return false;
  } catch {
    return false;
  }
}

function buildFirebaseConfig() {
  const config = { ...DEFAULT_FIREBASE_WEB_CONFIG };
  const map = [
    ["apiKey", "FIREBASE_API_KEY"],
    ["authDomain", "FIREBASE_AUTH_DOMAIN"],
    ["projectId", "FIREBASE_PROJECT_ID"],
    ["storageBucket", "FIREBASE_STORAGE_BUCKET"],
    ["messagingSenderId", "FIREBASE_MESSAGING_SENDER_ID"],
    ["appId", "FIREBASE_APP_ID"],
    ["measurementId", "FIREBASE_MEASUREMENT_ID"],
  ];
  for (const [key, envSuffix] of map) {
    const v = readEnv(envSuffix);
    if (v) config[key] = v;
  }
  if (
    !config.apiKey ||
    !config.projectId ||
    !config.appId ||
    !config.measurementId
  ) {
    return null;
  }
  // Snippet Web Firebase uniquement : :ios: / :android: ont d’autres flux GA4 (autres measurementId).
  if (!String(config.appId).includes(":web:")) {
    console.error(
      "[ANALYTICS_CONFIG] FIREBASE_APP_ID doit être celui de l’application Web (segment :web:). " +
        "Ne pas mélanger avec l’appId iOS/Android ; le measurementId doit être celui du flux Web associé à cette app.",
    );
    return null;
  }
  return config;
}

function afterWindowLoad() {
  try {
    if (typeof window === "undefined") {
      return Promise.resolve();
    }
    if (document.readyState === "complete") {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      try {
        window.addEventListener("load", () => resolve(), { once: true });
      } catch {
        resolve();
      }
    });
  } catch {
    return Promise.resolve();
  }
}

function sanitizeParams(params) {
  const out = {};
  try {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      const key = String(k).slice(0, 40);
      if (typeof v === "number" && Number.isFinite(v)) {
        out[key] = v;
      } else if (typeof v === "boolean") {
        out[key] = v ? "true" : "false";
      } else {
        out[key] = String(v).slice(0, 100);
      }
    }
  } catch {
    /* silencieux */
  }
  return out;
}

class AnalyticsService {
  constructor() {
    this.enabled = false;
    this.currentUser = null;
    /** Compte admin / interne : aucun event envoyé (comme filtre GA4 côté produit). */
    this._internalUser = false;
    /** @type {{ analytics: import('firebase/analytics').Analytics, logEvent: Function, setUserId: Function, setUserProperties: Function, setAnalyticsCollectionEnabled?: Function } | null} */
    this._client = null;
    this._bootstrapPromise = null;
    this._bootstrapAttempted = false;
  }

  /** Ne pas envoyer d’events (désactivé globalement, ou utilisateur interne/admin). */
  _shouldTrack() {
    try {
      if (isAnalyticsGloballyDisabled()) return false;
      if (this._internalUser) return false;
      return true;
    } catch {
      return false;
    }
  }

  _applyCollectionEnabled(enabled) {
    try {
      if (!this._client?.setAnalyticsCollectionEnabled || !this._client.analytics) {
        return;
      }
      this._client.setAnalyticsCollectionEnabled(
        this._client.analytics,
        enabled,
      );
    } catch {
      /* silencieux */
    }
  }

  async initialize() {
    try {
      await this._ensureClient();
    } catch {
      /* silencieux */
    }
  }

  async _ensureClient() {
    try {
      if (isAnalyticsGloballyDisabled()) {
        analyticsDiag("Désactivé (VITE_ANALYTICS_DISABLED)");
        return null;
      }
      if (this._client) {
        return this._client;
      }
      if (this._bootstrapPromise) {
        return await this._bootstrapPromise;
      }
      if (this._bootstrapAttempted) {
        return null;
      }
      this._bootstrapPromise = this._runBootstrapAfterLoad();
      const result = await this._bootstrapPromise;
      this._bootstrapPromise = null;
      return result;
    } catch {
      return null;
    }
  }

  async _runBootstrapAfterLoad() {
    this._bootstrapAttempted = true;
    try {
      if (isAnalyticsGloballyDisabled()) {
        return null;
      }
      await afterWindowLoad();
      if (typeof window === "undefined") {
        return null;
      }

      const firebaseConfig = buildFirebaseConfig();
      if (!firebaseConfig) {
        analyticsDiag(
          "Configuration Firebase incomplète — renseigner VITE_FIREBASE_API_KEY, PROJECT_ID, APP_ID, MEASUREMENT_ID",
        );
        return null;
      }

      const [appMod, analyticsMod] = await Promise.all([
        import("firebase/app"),
        import("firebase/analytics"),
      ]);

      const supported = await analyticsMod.isSupported().catch(() => false);
      if (!supported) {
        analyticsDiag(
          "Analytics non pris en charge par ce navigateur (isSupported=false)",
        );
        return null;
      }

      const app = resolveFirebaseApp(appMod, firebaseConfig);

      const analytics = analyticsMod.getAnalytics(app);

      const debugOn =
        readEnv("FIREBASE_ANALYTICS_DEBUG") === "true" ||
        readEnv("FIREBASE_ANALYTICS_DEBUG") === "1" ||
        readEnv("ANALYTICS_DEBUG_MODE") === "true" ||
        readEnv("ANALYTICS_DEBUG_MODE") === "1";
      if (debugOn && typeof analyticsMod.setDefaultEventParameters === "function") {
        try {
          analyticsMod.setDefaultEventParameters({ debug_mode: 1 });
        } catch {
          /* silencieux */
        }
      }

      this._client = {
        analytics,
        logEvent: analyticsMod.logEvent,
        setUserId: analyticsMod.setUserId,
        setUserProperties: analyticsMod.setUserProperties,
        setAnalyticsCollectionEnabled: analyticsMod.setAnalyticsCollectionEnabled,
      };
      this.enabled = true;
      analyticsDiag("Initialisé", {
        projectId: firebaseConfig.projectId,
        measurementId: firebaseConfig.measurementId,
      });

      if (this._internalUser) {
        try {
          analyticsMod.setAnalyticsCollectionEnabled(analytics, false);
        } catch {
          /* silencieux */
        }
      }

      return this._client;
    } catch (e) {
      analyticsDiag(
        "Échec bootstrap",
        e && typeof e === "object" && "message" in e ? e.message : e,
      );
      return null;
    }
  }

  async logEvent(eventName, params = {}) {
    try {
      if (!this._shouldTrack()) return;
      const c = await this._ensureClient();
      if (!c) return;
      const safe = sanitizeParams(params);
      c.logEvent(c.analytics, eventName, safe);
    } catch {
      /* silencieux */
    }
  }

  logScreenView(screenName, screenClass = null) {
    try {
      if (!this._shouldTrack()) return;
      void this.logEvent("screen_view", {
        screen_name: screenName,
        screen_class: screenClass || screenName,
      });
    } catch {
      /* silencieux */
    }
  }

  setUserId(userId) {
    try {
      if (!this._shouldTrack() && userId != null) return;
      void (async () => {
        try {
          const c = await this._ensureClient();
          if (!c) return;
          const id =
            userId === null || userId === undefined ? null : String(userId);
          c.setUserId(c.analytics, id);
        } catch {
          /* silencieux */
        }
      })();
    } catch {
      /* silencieux */
    }
  }

  setUserProperty(name, value) {
    try {
      if (!this._shouldTrack()) return;
      void (async () => {
        try {
          const c = await this._ensureClient();
          if (!c) return;
          c.setUserProperties(c.analytics, { [name]: String(value) });
        } catch {
          /* silencieux */
        }
      })();
    } catch {
      /* silencieux */
    }
  }

  setUserProperties(userData) {
    if (!userData) {
      return;
    }
    try {
      if (isInternalUserProfile(userData)) {
        this._internalUser = true;
        this.currentUser = null;
        this._applyCollectionEnabled(false);
        return;
      }

      this._internalUser = false;
      this._applyCollectionEnabled(true);

      this.currentUser = {
        id: userData.id,
        role: userData.role?.name || null,
        certified: Boolean(userData.certified),
      };

      if (userData.id) {
        this.setUserId(userData.id);
      }

      if (userData.role?.name) {
        this.setUserProperty("user_role", userData.role.name);
      }

      if (typeof userData.certified !== "undefined") {
        this.setUserProperty(
          "is_certified",
          userData.certified ? "true" : "false",
        );
      }

      if (userData.genre) {
        this.setUserProperty("genre", String(userData.genre));
      }

      if (userData.lieu_d_habitation?.libelle) {
        this.setUserProperty(
          "lieu_habitation",
          String(userData.lieu_d_habitation.libelle),
        );
      }

      if (typeof userData.first_authentificate !== "undefined") {
        this.setUserProperty(
          "first_auth",
          userData.first_authentificate ? "true" : "false",
        );
      }
    } catch {
      /* silencieux */
    }
  }

  clearUser() {
    try {
      this.currentUser = null;
      this._internalUser = false;
      void (async () => {
        try {
          const c = await this._ensureClient();
          if (!c) return;
          try {
            if (c.setAnalyticsCollectionEnabled) {
              c.setAnalyticsCollectionEnabled(c.analytics, true);
            }
          } catch {
            /* silencieux */
          }
          c.setUserId(c.analytics, null);
        } catch {
          /* silencieux */
        }
      })();
    } catch {
      /* silencieux */
    }
  }

  logLogin(method) {
    try {
      void this.logEvent("login", { method });
    } catch {
      /* silencieux */
    }
  }

  logSignUp(method) {
    try {
      void this.logEvent("sign_up", { method });
    } catch {
      /* silencieux */
    }
  }

  logSearch(searchTerm) {
    try {
      void this.logEvent("search", { search_term: searchTerm });
    } catch {
      /* silencieux */
    }
  }

  logShare(contentType, itemId) {
    try {
      void this.logEvent("share", {
        content_type: contentType,
        item_id: itemId,
      });
    } catch {
      /* silencieux */
    }
  }

  logReservation(logementId, amount, currency = "XOF") {
    try {
      void this.logEvent("begin_checkout", {
        item_id: logementId,
        value: amount,
        currency,
      });
    } catch {
      /* silencieux */
    }
  }

  logReservationComplete(reservationId, amount, currency = "XOF") {
    try {
      void this.logEvent("purchase", {
        transaction_id: reservationId,
        value: amount,
        currency,
      });
    } catch {
      /* silencieux */
    }
  }

  logAddToFavorites(logementId) {
    try {
      void this.logEvent("add_to_wishlist", { item_id: logementId });
    } catch {
      /* silencieux */
    }
  }

  logViewLogement(logementId, title, options = {}) {
    const params = {
      item_id: String(logementId),
      item_name: title || "Logement",
    };

    if (options.ville) params.item_location = options.ville;
    if (options.prix) params.price = options.prix;
    if (options.type) params.item_category = options.type;
    if (options.hoteId) params.hote_id = String(options.hoteId);

    if (this.currentUser?.role) {
      params.user_role = this.currentUser.role;
    }

    try {
      void this.logEvent("view_item", params);
    } catch {
      /* silencieux */
    }
  }

  logViewLogementComplete(logement) {
    if (!logement) return;

    const params = {
      item_id: String(logement.id),
      item_name: logement.libelle || logement.title || "Logement",
    };

    if (logement.tarif) params.tarif = logement.tarif;
    if (logement.type_de_logements?.libelle) {
      params.type_de_logement = logement.type_de_logements.libelle;
    }

    if (this.currentUser?.role) {
      params.user_role = this.currentUser.role;
    }

    try {
      void this.logEvent("view_item", params);
    } catch {
      /* silencieux */
    }
  }

  logAddToFavoritesComplete(logementId, options = {}) {
    const params = {
      item_id: String(logementId),
    };

    if (options.titre) params.item_name = options.titre;
    if (options.ville) params.item_location = options.ville;
    if (options.prix) params.price = options.prix;

    if (this.currentUser?.role) {
      params.user_role = this.currentUser.role;
    }

    try {
      void this.logEvent("add_to_wishlist", params);
    } catch {
      /* silencieux */
    }
  }

  logSearchComplete(searchTerm, filters = {}) {
    const params = {
      search_term: searchTerm || "",
    };

    if (filters.ville) params.filter_ville = filters.ville;
    if (filters.prixMin) params.filter_prix_min = filters.prixMin;
    if (filters.prixMax) params.filter_prix_max = filters.prixMax;
    if (filters.dateDebut) params.filter_date_debut = filters.dateDebut;
    if (filters.dateFin) params.filter_date_fin = filters.dateFin;
    if (filters.nombreVoyageurs) {
      params.filter_voyageurs = filters.nombreVoyageurs;
    }
    if (filters.typeLogement) params.filter_type = filters.typeLogement;
    if (filters.nombreResultats) {
      params.result_count = filters.nombreResultats;
    }

    if (this.currentUser?.role) {
      params.user_role = this.currentUser.role;
    }

    try {
      void this.logEvent("search", params);
    } catch {
      /* silencieux */
    }
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;
